// PPCConsultancyPage.jsx
import React, { useEffect } from 'react';
import { Row, Col, Button, Table } from 'reactstrap';
import { Link } from 'react-router-dom';
import xsimage from './paidxs.png'

const PPCConsultancyPage = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
        // Scroll animation observer
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.style.animation = 'fadeInUp 0.8s ease-out forwards';
                }
            });
        }, { threshold: 0.1 });

        document.querySelectorAll('[data-animated]').forEach(el => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    const ppcConsultancyStats = [
        { number: '40%', label: 'average ROI increase from strategic PPC consultancy' },
        { number: '35%', label: 'reduction in wasted ad spend after expert audit' },
        { number: '2.5x', label: 'improvement in conversion rates with optimized campaigns' },
        { number: '68%', label: 'of businesses see better results within 60 days of consultancy' }
    ];

    const ppcConsultancyApproaches = [
        {
            icon: 'fas fa-clipboard-check',
            title: 'Full PPC Audit & Performance Assessment',
            desc: 'Comprehensive review of existing paid search accounts including performance benchmarking, account structure diagnosis, keyword evaluation, and bid strategy assessment.'
        },
        {
            icon: 'fas fa-chess',
            title: 'Custom PPC Strategy Development',
            desc: 'Data-driven strategic planning tailored to business objectives with forward-looking recommendations, long-term growth planning, and conversion optimization roadmaps.'
        },
        {
            icon: 'fas fa-chart-bar',
            title: 'Competitor & Market Intelligence Analysis',
            desc: 'In-depth competitive landscape analysis including competitor keyword strategies, market trend benchmarking, and opportunity identification for competitive advantage.'
        },
        {
            icon: 'fas fa-graduation-cap',
            title: 'PPC Training & Team Enablement',
            desc: 'Comprehensive training workshops, performance best practices, tools and frameworks guidance to upskill in-house teams for effective campaign management.'
        },
        {
            icon: 'fas fa-handshake',
            title: 'Implementation Support & Ongoing Advisory',
            desc: 'Practical implementation assistance, performance review sessions, and iterative optimization guidance to ensure strategic recommendations translate to real results.'
        },
        {
            icon: 'fas fa-road',
            title: 'Growth Roadmapping & Scalability Planning',
            desc: 'Strategic roadmaps for scaling paid search efforts, market expansion planning, and infrastructure development for sustainable long-term growth.'
        }
    ];

    const benefits = [
        ['Strategic Clarity & Focus', 'Identify exactly where to allocate PPC budget for maximum impact and alignment with business objectives'],
        ['Improved Advertising Efficiency', 'Reduce wasted ad spend, lower cost per acquisition, and optimize campaign performance through expert guidance'],
        ['Actionable Optimization Roadmaps', 'Receive clear, step-by-step implementation plans to enhance performance and overcome performance plateaus'],
        ['Competitive Intelligence Advantage', 'Gain insights into competitor strategies, market positioning, and untapped opportunities in your industry'],
        ['Enhanced Internal Team Capability', 'Empower your team with industry best practices, strategic frameworks, and practical skills for sustainable success']
    ];

    const targetBusinesses = [
        'Businesses with existing PPC campaigns that are underperforming or not meeting established goals',
        'Brands seeking to scale paid search performance and break through current performance plateaus',
        'In-house marketing teams requiring strategic guidance, upskilling, or external validation of approaches',
        'Companies preparing for market expansion, product launches, or entering new competitive landscapes',
        'Organizations ready to move beyond basic optimization to advanced strategic paid search management',
        'Businesses wanting to maximize ROI from existing PPC investments through expert strategic oversight'
    ];

    const ppcConsultancyQuestions = [
        'How can you optimize your current PPC campaigns for better ROI and efficiency?',
        'What strategic adjustments will have the greatest impact on your paid search performance?',
        'How do your competitors\' PPC strategies compare to yours, and where are the opportunities?',
        'What training and frameworks would empower your team to manage PPC more effectively?',
        'How can you build a scalable paid search infrastructure for sustainable long-term growth?'
    ];

    return (
        <div className="paidmedia sv-page">
            <div className="sv-decorative-image-container">

            </div>

            <div className='xs-sv-page'>
                <img src={xsimage} />
            </div>
            {/* Page Title Section */}
            <div className="sv-page-title-section">
                <div className="container">
                    <h1>
                        <span className="sv-gradient-text">PPC Consultancy</span> Services
                    </h1>
                    <p>Expert guidance, strategic optimization, and actionable roadmaps to maximize your PPC ROI</p>
                </div>
            </div>

            {/* Main Content */}
            <div className="container-fluid sv-content-wrapper">
                <div className="sv-main-content">
                    {/* Hero Content Section */}
                    <section className="sv-hero-section" data-animated>
                        <Row className="align-items-center">
                            <Col lg={8}>
                                <p className="sv-lead">
                                    Pay-Per-Click (PPC) advertising can be highly effective, but without the right strategy, even significant ad spend can underperform.
                                </p>
                                <p className="sv-description">
                                    <strong>PPC Consultancy</strong> helps businesses refine their paid search approach, uncover optimisation opportunities, and make smarter decisions that drive higher returns on investment. At Reachtix, our PPC consultancy services combine data-driven insights with strategic expertise to elevate your paid search performance.
                                </p>
                                <p className="sv-description">
                                    With the PPC landscape constantly shifting — platforms, bidding behaviors, and audience trends evolving — expert insight becomes essential. Without it, businesses risk overspending, targeting the wrong keywords, or missing scalable opportunities. PPC consultancy brings strategic clarity and expert evaluation so your paid search efforts are efficient, measurable, and effective.
                                </p>
                                <div className="sv-cta-buttons">
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='dark'
                                    >
                                        <i className="fas fa-rocket mr-2"></i>
                                        Get PPC Consultancy
                                    </Button>
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='dark'
                                    >
                                        <i className="fas fa-chart-line mr-2"></i>
                                        Book PPC Consultation
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Stats Section */}
                    <section className="sv-stats-section">
                        <h2 className="sv-section-title" data-animated>
                            The <span className="sv-text-gradient">Consultancy Performance Impact</span>
                        </h2>
                        <Row className="sv-stats-grid">
                            {ppcConsultancyStats.map((stat, index) => (
                                <Col lg={6} md={6} key={index} className="mb-4" data-animated>
                                    <div className="sv-stat-card">
                                        <div className="sv-stat-number">{stat.number}</div>
                                        <div className="sv-stat-label">{stat.label}</div>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </section>

                    {/* What Is PPC Consultancy Section */}
                    <section className="sv-explanation-section" data-animated>
                        <Row>
                            <Col lg={8}>
                                <h2 className="sv-section-title">What Is PPC Consultancy?</h2>
                                <p className="sv-section-description">
                                    PPC consultancy is a specialized advisory service focused on helping brands improve their paid search campaigns through strategic optimization, expert recommendations, and actionable roadmaps. Unlike full campaign management, consultancy zeroes in on strategic oversight and expert guidance to ensure PPC tactics align with business goals.
                                </p>
                                <p className="sv-section-description">
                                    This service is designed for businesses seeking to lower acquisition costs, boost conversion rates, improve campaign efficiency, or break through performance plateaus with expert strategic insight rather than day-to-day management.
                                </p>
                                <div className="sv-questions-grid">
                                    <h4>Key PPC Consultancy Questions We Address:</h4>
                                    {ppcConsultancyQuestions.map((question, index) => (
                                        <div key={index} className="sv-question-item">
                                            <i className="fas fa-question-circle"></i>
                                            {question}
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Why PPC Consultancy Section */}
                    <section className="sv-benefits-overview" data-animated>
                        <Row>
                            <Col xl={12}>
                                <h2 className="sv-section-title">Why PPC Consultancy Drives Strategic Excellence</h2>
                                <p className="sv-section-description">
                                    PPC consultancy transforms paid search from tactical execution to strategic advantage, enabling organizations to optimize existing investments, develop competitive capabilities, and build sustainable performance frameworks.
                                </p>
                                <div className="sv-benefits-cards">
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-clipboard-check"></i>
                                        <h5>Performance Diagnosis</h5>
                                        <p>Comprehensive audits to identify optimization opportunities and performance gaps</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-chess"></i>
                                        <h5>Strategic Planning</h5>
                                        <p>Data-driven strategy development aligned with business objectives and market realities</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-chart-line"></i>
                                        <h5>ROI Optimization</h5>
                                        <p>Expert guidance to maximize return on advertising spend and improve efficiency</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-users"></i>
                                        <h5>Team Empowerment</h5>
                                        <p>Training and frameworks to build internal capabilities and sustainable success</p>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Our PPC Consultancy Approach Section */}
                    <section className="sv-approach-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="sv-section-title">Our PPC Consultancy Capabilities at Reachtix</h2>
                                <p className="sv-section-description">
                                    We tailor our consultancy services to your business maturity and goals — whether you need strategic refinement, comprehensive audits, competitive intelligence, or team development support for sustainable paid search success.
                                </p>

                                <div className="sv-approaches-grid">
                                    {ppcConsultancyApproaches.map((item, index) => (
                                        <div key={index} className="sv-approach-card">
                                            <div className="sv-approach-icon">
                                                <i className={item.icon}></i>
                                            </div>
                                            <div className="sv-approach-content">
                                                <h4>{item.title}</h4>
                                                <p>{item.desc}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Benefits Section */}
                    <section className="sv-benefits-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="mt-5 sv-section-title">Benefits of PPC Consultancy with Reachtix</h2>
                                <div className="table-responsive">
                                    <Table borderless className="sv-benefits-table">
                                        <thead>
                                            <tr>
                                                <th>Strategic Advantage</th>
                                                <th>Business Impact</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {benefits.map(([advantage, impact], index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <i className="fas fa-check-circle"></i>
                                                        <strong>{advantage}</strong>
                                                    </td>
                                                    <td>{impact}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Who Should Invest Section */}
                    <section className="sv-target-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="sv-section-title">Who Benefits Most from PPC Consultancy?</h2>
                                <p className="sv-section-description">
                                    PPC consultancy delivers maximum value for organizations seeking expert guidance to optimize existing investments, develop competitive advantages, and build sustainable paid search capabilities:
                                </p>
                                <div className="sv-target-list">
                                    {targetBusinesses.map((business, index) => (
                                        <div key={index} className="sv-target-item">
                                            <i className="fas fa-chevron-right"></i>
                                            {business}
                                        </div>
                                    ))}
                                </div>
                                <p className="sv-section-description mt-4">
                                    Whether you're early in your PPC journey or seeking to break through performance plateaus, expert consultancy establishes the foundation for smarter, results-driven paid search with sustainable long-term success.
                                </p>
                            </Col>
                        </Row>
                    </section>

                    {/* Final CTA Section */}
                    <section className="sv-main-cta" data-animated>
                        <Row>
                            <Col>
                                <h2>Ready to Optimize Your Paid Search Performance?</h2>
                                <p className="sv-cta-description">
                                    If your PPC campaigns could deliver better returns, it's time for targeted insight and expert guidance. Reachtix's <strong>PPC Consultancy services</strong> help you unlock performance opportunities, improve ROI, and build smarter paid search strategies for sustainable growth.
                                </p>
                                <p className="sv-cta-description">
                                    At Reachtix, we treat PPC consultancy as a strategic investment — not just advisory. Our specialists blend analytical rigor with business insight to make your paid search a powerful driver of measurable improvements and competitive advantage.
                                </p>
                                <div className="sv-cta-buttons">
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='primary'
                                    >
                                        <i className="fas fa-handshake mr-2"></i>
                                        Get PPC Consultancy
                                    </Button>
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='light'
                                    >
                                        <i className="fas fa-search mr-2"></i>
                                        Book Free Consultation
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default PPCConsultancyPage;